import React from 'react'

import { PortableTextBlock } from 'next-sanity'

import PortableText from '@/app/components/PortableText'
import PortableTextMinimal from '@/app/components/PortableTextMinimal'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import { generateElementId } from '@/app/utils/text'
import type { FaqQueryResult } from '@/sanity.types'

type FAQGroupProps = {
  categories: NonNullable<FaqQueryResult>['categories']
  className?: string
}

export function FAQGroup({ categories, className }: FAQGroupProps) {
  if (!categories || categories.length === 0) {
    return null
  }

  return (
    <div className={cn('p-5xl rounded-xl bg-neutral-50 w-full lg:p-9xl', className)}>
      {categories.map((category, categoryIndex) => (
        <React.Fragment key={category._id}>
          <div className={cn(categoryIndex > 0 && 'mt-3xl lg:mt-6xl')}>
            <p
              className={cn(
                'size-subheading-md font-bold text-primary-700 mb-3xl lg:mb-6xl',
                'lg:size-subheading-xl',
              )}
            >
              {category.title}
            </p>

            {category.items && category.items.length > 0 ? (
              <div className="flex flex-col gap-xl lg:gap-3xl">
                {category.items
                  .filter(
                    (item) =>
                      item.question && Array.isArray(item.question) && item.question.length > 0,
                  )
                  .map((item, index, filteredItems) => (
                    <React.Fragment key={item._id}>
                      <div className="flex flex-col gap-md lg:gap-xl">
                        <p
                          id={generateElementId(item.question as PortableTextBlock[])}
                          className="size-subheading-sm font-bold text-neutral-900 lg:size-subheading-lg"
                        >
                          <PortableTextMinimal
                            value={item.question as PortableTextBlock[]}
                            tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
                          />
                        </p>
                        {item.answer?.length && (
                          <div className="[&>div>p:last-child]:mb-0">
                            <PortableText
                              value={item.answer as PortableTextBlock[]}
                              tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
                              paragraphClassName="size-body-sm text-neutral-900 lg:size-body-lg"
                            />
                          </div>
                        )}
                      </div>
                      {index < filteredItems.length - 1 && <hr className="my-0" />}
                    </React.Fragment>
                  ))}
              </div>
            ) : (
              <p className="size-body-lg text-neutral-900">No items in this category</p>
            )}
          </div>
          {categoryIndex < categories.length - 1 && <hr className="mt-xl lg:mt-3xl" />}
        </React.Fragment>
      ))}
    </div>
  )
}
